/**
 * Admin 全球玩家(表) — full binary downline table from PHP network root.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Input, Modal, Space, Table, Typography, message } from 'antd';
import type { MenuProps } from 'antd';
import { api, ApiError } from '../../api/client';
import { formatBeijingTime } from '@uo/shared';
import { useAuthStore } from '../../store/auth';
import { DeleteUserForm } from '../../components/DeleteUserModal';
import { DirectFriendsModal } from '../../components/DirectFriendsPanel';
import { MigrateUserModal } from '../../components/MigrateUserModal';
import { PerfOpsForm } from '../../components/PerfOpsModal';

type GlobalTableRow = {
  serial: string;
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  registered_at?: string | null;
  parent_number?: string | null;
  referrer_number?: string | null;
  left_total: string;
  left_reinvest: string;
  right_total: string;
  right_reinvest: string;
  star_count: number;
  depth: number;
};

type GlobalTableResult = {
  root_user_id: number;
  total: number;
  has_more: boolean;
  page: number;
  limit: number;
  rows: GlobalTableRow[];
};

type NetworkRoot = {
  id: number;
  username: string;
  level_code: string;
  player_number: string | null;
};

type ActionTarget = { userId: number; username: string };

function fmtSum(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const PAGE_SIZE = 100;
/** Admin 全球玩家表默认展开层数（相对当前根）。 */
const VISIBLE_DEPTH = 20;

export function AdminGlobalPlayersTable() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canMigrate = hasNode('platform.user.shift_upline');
  const canDelete = hasNode('platform.user.delete');
  const canPerf = canMigrate;

  const [rootFilter, setRootFilter] = useState(() => searchParams.get('root')?.trim() || '');
  const [rootUserId, setRootUserId] = useState<number | undefined>();
  const [search, setSearch] = useState(() => searchParams.get('root')?.trim() || '');
  const [page, setPage] = useState(1);
  const [drillLoading, setDrillLoading] = useState(false);
  const [migrateTarget, setMigrateTarget] = useState<ActionTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ActionTarget | null>(null);
  const [perfTarget, setPerfTarget] = useState<ActionTarget | null>(null);
  const [friendsTarget, setFriendsTarget] = useState<
    (ActionTarget & { playerNumber: string | null }) | null
  >(null);

  const { data: networkRoot } = useQuery({
    queryKey: ['admin-binary-tree-network-root'],
    queryFn: () => api.get<NetworkRoot>('/api/admin/binary-tree/network-root'),
    staleTime: 300_000,
  });

  useEffect(() => {
    setPage(1);
  }, [rootFilter, rootUserId]);

  const tableQuery = useQuery({
    queryKey: ['admin-binary-tree-global-table', rootFilter, rootUserId ?? null, page, VISIBLE_DEPTH],
    queryFn: () =>
      api.get<GlobalTableResult>('/api/admin/binary-tree/global-table', {
        ...(rootUserId != null && rootUserId > 0
          ? { root_user_id: rootUserId }
          : rootFilter
            ? { root_player_number: rootFilter }
            : {}),
        page,
        limit: PAGE_SIZE,
        max_depth: VISIBLE_DEPTH,
      }),
    staleTime: 90_000,
    retry: 1,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!tableQuery.isFetching) setDrillLoading(false);
  }, [tableQuery.isFetching, tableQuery.dataUpdatedAt]);

  function applyRootFilter(playerNumber: string, userId?: number) {
    setDrillLoading(true);
    setRootFilter(playerNumber.trim());
    setRootUserId(userId != null && userId > 0 ? userId : undefined);
    setSearch('');
    setPage(1);
  }

  function runSearch() {
    const q = search.trim();
    if (/^\d+$/.test(q)) {
      applyRootFilter(q);
      return;
    }
    if (!q) applyRootFilter('');
    else message.info('请输入玩家 ID 数字');
  }

  function refreshTable() {
    void qc.invalidateQueries({
      queryKey: ['admin-binary-tree-global-table'],
      refetchType: 'active',
    });
  }

  function openFriends(row: GlobalTableRow) {
    setFriendsTarget({
      userId: row.user_id,
      username: row.username,
      playerNumber: row.player_number,
    });
  }

  function accountMenu(row: GlobalTableRow): MenuProps['items'] {
    const items: MenuProps['items'] = [
      {
        key: 'drill',
        label: '下钻此账户子树',
        onClick: () => applyRootFilter(row.player_number ?? String(row.user_id), row.user_id),
      },
      {
        key: 'friends',
        label: '好友（主号直推）',
        onClick: () => openFriends(row),
      },
    ];
    if (canMigrate) {
      items.push({
        key: 'migrate',
        label: '迁移（挂点 / 推荐线）',
        onClick: () => setMigrateTarget({ userId: row.user_id, username: row.username }),
      });
    }
    if (canPerf) {
      items.push({
        key: 'perf',
        label: '业绩（清零 / 重算 / 钉死）',
        onClick: () => setPerfTarget({ userId: row.user_id, username: row.username }),
      });
    }
    if (canDelete) {
      items.push({
        key: 'delete',
        label: '删除',
        danger: true,
        onClick: () => setDeleteTarget({ userId: row.user_id, username: row.username }),
      });
    }
    return items;
  }

  const rows = tableQuery.data?.rows ?? [];
  const total = tableQuery.data?.total ?? 0;
  const hasMore = tableQuery.data?.has_more ?? false;
  const showDrillSkeleton = drillLoading && tableQuery.isFetching;

  const columns = [
    {
      title: '序号',
      dataIndex: 'serial',
      width: 72,
    },
    {
      title: '玩家 ID',
      width: 140,
      render: (_: unknown, row: GlobalTableRow) => (
        <Dropdown menu={{ items: accountMenu(row) }} trigger={['click']}>
          <Button type="link" size="small" style={{ padding: 0 }}>
            {'★'.repeat(row.star_count)}
            {row.player_number ?? row.user_id}
          </Button>
        </Dropdown>
      ),
    },
    {
      title: '昵称',
      width: 160,
      ellipsis: true,
      render: (_: unknown, row: GlobalTableRow) => (
        <Dropdown menu={{ items: accountMenu(row) }} trigger={['click']}>
          <Button type="link" size="small" style={{ padding: 0, maxWidth: '100%' }}>
            {row.nickname ?? row.username}
          </Button>
        </Dropdown>
      ),
    },
    {
      title: '上级推荐人',
      width: 110,
      render: (_: unknown, row: GlobalTableRow) => row.parent_number ?? '—',
    },
    {
      title: '节点推荐人',
      width: 110,
      render: (_: unknown, row: GlobalTableRow) => row.referrer_number ?? '—',
    },
    {
      title: '注册时间',
      width: 150,
      render: (_: unknown, row: GlobalTableRow) => formatBeijingTime(row.registered_at),
    },
    {
      title: 'L(总)',
      width: 88,
      align: 'right' as const,
      render: (_: unknown, row: GlobalTableRow) => fmtSum(row.left_total),
    },
    {
      title: 'L(复)',
      width: 88,
      align: 'right' as const,
      render: (_: unknown, row: GlobalTableRow) => fmtSum(row.left_reinvest),
    },
    {
      title: 'R(总)',
      width: 88,
      align: 'right' as const,
      render: (_: unknown, row: GlobalTableRow) => fmtSum(row.right_total),
    },
    {
      title: 'R(复)',
      width: 88,
      align: 'right' as const,
      render: (_: unknown, row: GlobalTableRow) => fmtSum(row.right_reinvest),
    },
    {
      title: '深度',
      dataIndex: 'depth',
      width: 56,
    },
    {
      title: '操作',
      width: canMigrate || canDelete || canPerf ? 240 : 72,
      fixed: 'right' as const,
      render: (_: unknown, row: GlobalTableRow) => (
        <Space size={0} wrap>
          <Button type="link" size="small" onClick={() => openFriends(row)}>
            好友
          </Button>
          {canMigrate ? (
            <Button
              type="link"
              size="small"
              onClick={() => setMigrateTarget({ userId: row.user_id, username: row.username })}
            >
              迁移
            </Button>
          ) : null}
          {canPerf ? (
            <Button
              type="link"
              size="small"
              onClick={() => setPerfTarget({ userId: row.user_id, username: row.username })}
            >
              业绩
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => setDeleteTarget({ userId: row.user_id, username: row.username })}
            >
              删除
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        全网安置表（与前台「全球玩家(表)」相同算法，默认显示 {VISIBLE_DEPTH} 层）。默认从 PHP 网体根节点
        {networkRoot ? (
          <>
            {' '}
            <Typography.Text strong>{networkRoot.username}</Typography.Text> (#{networkRoot.id})
          </>
        ) : (
          ' …'
        )}
        展开。点击玩家 ID / 昵称可选：下钻、好友、迁移、业绩、删除；右侧操作列也可直接点。
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          placeholder="搜索玩家 ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={runSearch}
          style={{ width: 160 }}
        />
        <Button onClick={runSearch}>搜索</Button>
        {rootFilter ? (
          <Button type="link" onClick={() => applyRootFilter('')}>
            清除筛选（回到全网根）
          </Button>
        ) : null}
      </Space>

      {rootFilter ? (
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
          当前根节点 ID：{rootFilter}
        </Typography.Text>
      ) : null}

      <Table<GlobalTableRow>
        size="small"
        rowKey={(r) => `${r.serial}-${r.user_id}`}
        loading={tableQuery.isLoading || (tableQuery.isFetching && !showDrillSkeleton)}
        columns={columns}
        dataSource={showDrillSkeleton ? [] : rows}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: hasMore ? page * PAGE_SIZE + 1 : total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
        scroll={{ x: canMigrate || canDelete || canPerf ? 1200 : 1020 }}
        locale={{
          emptyText: showDrillSkeleton
            ? '加载旗下网体…'
            : tableQuery.isError
              ? tableQuery.error instanceof ApiError
                ? tableQuery.error.message
                : '加载失败，请刷新'
              : '暂无数据',
        }}
      />

      <DirectFriendsModal
        open={friendsTarget !== null}
        userId={friendsTarget?.userId}
        username={friendsTarget?.username}
        playerNumber={friendsTarget?.playerNumber}
        onClose={() => setFriendsTarget(null)}
        onOpenPlayer={(num, uid) => {
          setFriendsTarget(null);
          applyRootFilter(num, uid);
        }}
      />

      {migrateTarget ? (
        <MigrateUserModal
          open
          userId={migrateTarget.userId}
          username={migrateTarget.username}
          onClose={() => setMigrateTarget(null)}
          onDone={refreshTable}
        />
      ) : null}

      <Modal
        open={perfTarget !== null}
        title={`业绩运维 — ${perfTarget?.username ?? ''}`}
        footer={null}
        onCancel={() => setPerfTarget(null)}
        destroyOnClose
        width={720}
      >
        {perfTarget ? (
          <PerfOpsForm
            userId={perfTarget.userId}
            username={perfTarget.username}
            onDone={() => {
              setPerfTarget(null);
              refreshTable();
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={deleteTarget !== null}
        title={`删除会员 — ${deleteTarget?.username ?? ''}`}
        footer={null}
        onCancel={() => setDeleteTarget(null)}
        destroyOnClose
        width={920}
      >
        {deleteTarget ? (
          <DeleteUserForm
            userId={deleteTarget.userId}
            username={deleteTarget.username}
            onDone={() => {
              setDeleteTarget(null);
              refreshTable();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}
